import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button, Checkbox, Drawer, FormControlLabel, MenuItem, Stack, Typography } from "@mui/material";
import { AsyncContentMui, ProgressOverlay, RoleComponentGuard, SimpleHeadMui } from "components";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useCannotMoveToStageHook, useIsManagerUser, useModals } from "hooks";
import {
  useAccountSelector,
  useCharacteristicManageData,
  useDealActions,
  useDealIdSelector,
  usePickUserPermissions,
  useUserRoleSelector
} from "store";
import { UserPermissionType } from "store/types";

import { defaultState, FormContext, IViewBy, State } from ".";
import { RoleName } from "../../../constants";
import { AssetReport } from "./AssetReport";
import { CategoriesTabs } from "./CategoriesTabs";
import { Category } from "./Category";
import { ContactList } from "./ContactList";
import { CreateChar } from "./CreateChar";
import { useCharDataHooks } from "./hooks/use-char-data-hooks";
import { useCharHooks } from "./hooks/use-char-hooks";
import { SelectField } from "./SelectField";
import { ViewSelector } from "./ViewSelector";

function findScrollParent(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (
      parent.scrollHeight > parent.clientHeight &&
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay")
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

export const DealFormDrawer: React.FC = () => {
  const { show_debt, investor_level_characteristics } = useAccountSelector() || {};
  const {
    getDealCharacteristicsDataAction,
    setCharacteristicsManageAction,
    postDealCharacteristicsDataAction,
    getAssetClassesAction,
    getDealAndStoreAction,
    updateDealAction
  } = useDealActions();
  const { errorModal } = useModals();
  const { showCannotMoveToStageModal } = useCannotMoveToStageHook();

  const isDealContext = useDealIdSelector();
  const isManagerUser = useIsManagerUser();
  const featurePermission = usePickUserPermissions("characteristics");

  const userRole = useUserRoleSelector();
  const isAdminRole = userRole?.name === RoleName.Admin;
  const featureViewOnly = featurePermission === UserPermissionType.VIEWER;
  const featureAdmin = featurePermission === UserPermissionType.ADMIN;

  const {
    dealId,
    dealName,
    viewOnly,
    onSubmitSuccess,
    characteristicId,
    isContact,
    targetStageName,
    requiredForStageId,
    missingFieldNames: missingFieldNamesForContext
  } = useCharacteristicManageData() || {};

  const [state, setState] = useState<State>(defaultState);
  const [viewBy, setViewBy] = useState<IViewBy | null>(null);

  const submitBtnRef = useRef<HTMLButtonElement>(null);

  const {
    data: assetClasses,
    loading: assetClassesLoading,
    action: getAssetClasses
  } = useActionHook(getAssetClassesAction);

  const subAssetClasses = useMemo(
    () => assetClasses?.find((item) => item.id === state.assetClassId)?.sub_asset_class || [],
    [assetClasses, state.assetClassId]
  );

  const { createChar, updateChar, deleteChar, getCharacteristics } = useCharHooks({ state, setState });
  const {
    updateCharData,
    buildNewCharData,
    handleManyCharData,
    getOneData,
    getManyData,
    overrideCharacteristicData,
    isOverrideAvailable
  } = useCharDataHooks({
    state,
    setState
  });

  //----------need to scroll to the characteristic if characteristicId passed ----------
  const characteristicRef = useRef<HTMLDivElement | null>(null);
  const missingFieldRefsMapRef = useRef<Record<string, HTMLDivElement | null>>({});
  const hasScrolledToMissingRef = useRef(false);

  const setCharacteristicRef = (e: HTMLDivElement, id: number | string) => {
    if (id == characteristicId) {
      characteristicRef.current = e;
    }
  };

  const {
    action: getData,
    data,
    loading
  } = useActionHook(getDealCharacteristicsDataAction, {
    onSuccess: (res) => {
      const characteristic = res.characteristics.find((item) => item.id === characteristicId);
      // Only keep sub_asset_class if it belongs to the current asset_class (avoids sending stale sub id)
      const subBelongsToAsset =
        res.sub_asset_class?.id != null &&
        res.asset_class?.id != null &&
        res.sub_asset_class?.parent_id === res.asset_class?.id;

      // When opening for "Review missing fields", start on the tab that contains the first missing field so scroll+focus work
      const firstMissingName = missingFieldNamesForContext?.[0]?.trim().toLowerCase();
      const firstMissingChar = firstMissingName
        ? res.characteristics.find((c) => c.name.trim().toLowerCase() === firstMissingName)
        : undefined;
      const initialCategory =
        firstMissingChar?.category_name ?? characteristic?.category_name ?? (isContact ? "Contact" : "General");

      setState({
        category: initialCategory,
        categories: res.categories,
        subCategories: res.subcategories,
        characteristics: res.characteristics.map((item) => ({
          ...item,
          is_deleted: false,
          category_name: item.category_name
        })),
        data: res.characteristic_data.map((item) => ({ ...item, is_deleted: false })),
        contact: res.contact ? res.contact : [],
        assetClassId: res.asset_class.id,
        subAssetClassId: subBelongsToAsset ? res.sub_asset_class!.id : null,
        showAssetClass: res.show_asset_class,
        characteristic_subcategory_id: res.characteristic_subcategory_id,
        showSubAssetClass: res.show_sub_asset_class,
        isDebt: res.type === "debt",
        hasSubDeals: res.has_sub_deals
      });
      // When opening for a specific characteristic (not "review missing fields"), scroll to it after content mounts
      if (characteristicId && !missingFieldNamesForContext?.length) {
        setTimeout(() => {
          characteristicRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 400);
      }
    }
  });

  const getValidationMessage = (err: unknown): string => {
    const e = err as {
      errors?: Record<string, string[]>;
      message?: string;
      response?: { data?: { errors?: Record<string, string[]>; message?: string } };
    };
    const data = e?.response?.data ?? e;
    const errors = data?.errors;
    if (errors && typeof errors === "object" && Object.keys(errors).length) {
      const first = Object.values(errors)
        .flat()
        .find((m): m is string => typeof m === "string");
      if (first) return first;
    }
    return data?.message ?? e?.message ?? "Something went wrong";
  };

  const { action: postData, loading: postLoading } = useActionHook(postDealCharacteristicsDataAction, {
    onSuccess: () => {
      // When "Apply and move to XYZ", onSubmitSuccess is called after the move in handleSubmit
      if (!targetStageName) onSubmitSuccess?.();
      if (isDealContext) {
        getDealAndStoreAction(isDealContext);
      }
    }
    // onReject omitted: handleSubmit catch shows the modal so we don't double-show and flow stays in catch
  });

  const handleChangeTab = useCallback(
    (value: string) => {
      setState((prevState) => ({ ...prevState, category: value }));
    },
    [setState]
  );

  const handleShowAssets = ({
    field,
    value
  }: {
    field: keyof Pick<State, "assetClassId" | "subAssetClassId" | "showAssetClass" | "showSubAssetClass">;
    value: boolean | number | null;
  }) => {
    setState((prevState) => ({
      ...prevState,
      [field]: value,
      ...(field === "assetClassId" && { subAssetClassId: null })
    }));
  };

  const handleClose = () => {
    setCharacteristicsManageAction(null);
    setState(defaultState);
  };

  const handleSubmit = useCallback(async () => {
    if (!dealId) return;
    if (!state.assetClassId) return;

    // When "Apply and move to stage", validate that all required (yellow) fields are filled first
    if (targetStageName && requiredForStageId && missingFieldNamesForContext?.length) {
      const stillEmptyRequiredNames = missingFieldNamesForContext.filter((requiredName) => {
        const char = state.characteristics.find(
          (c) => c.name.trim().toLowerCase() === requiredName.trim().toLowerCase()
        );
        if (!char) return false;
        if (char.is_deleted) return false; // archived characteristics are ignored
        if (char.is_multiple) {
          const items = state.data.filter((d) => d.characteristic_id === char.id && !d.is_deleted);
          const hasValue = items.some((item) => item.value?.trim() !== "");
          return !hasValue;
        }
        const item = state.data.find((d) => d.characteristic_id === char.id && !d.is_deleted);
        const value = item?.value?.trim() ?? "";
        return value === "";
      });
      if (stillEmptyRequiredNames.length) {
        const firstEmptyName = stillEmptyRequiredNames[0];
        const key = Object.keys(missingFieldRefsMapRef.current).find(
          (k) => k.trim().toLowerCase() === firstEmptyName.trim().toLowerCase()
        );
        const el = key ? missingFieldRefsMapRef.current[key] : null;
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        showCannotMoveToStageModal({
          dealId,
          targetStageName: targetStageName ?? "this stage",
          targetStageId: Number(requiredForStageId),
          missingFields: stillEmptyRequiredNames,
          alreadyOnDeal: true
        });
        return;
      }
    }

    try {
      // Only send sub_asset_class_id if it's in the current asset's sub list (avoids 422 when stale)
      const validSubId =
        state.subAssetClassId != null && subAssetClasses.some((s) => s.id === state.subAssetClassId)
          ? state.subAssetClassId
          : null;

      await postData({
        deal_id: dealId,

        show_asset_class: state.showAssetClass,
        show_sub_asset_class: state.showSubAssetClass,

        asset_class_id: state.assetClassId,
        sub_asset_class_id: validSubId,

        type: state.isDebt ? "debt" : "",
        has_sub_deals: state.hasSubDeals,

        characteristics: state.characteristics,
        characteristic_data: state.data,
        contact: (state.contact || []).map((contact) =>
          typeof contact.id === "string" ? { ...contact, id: undefined } : contact
        ),

        characteristic_subcategory_id: state.characteristic_subcategory_id
      });
    } catch (err) {
      errorModal({ msg: getValidationMessage(err) });
      return;
    }

    if (targetStageName && requiredForStageId) {
      try {
        await updateDealAction({
          dealId,
          deal_stage_id: Number(requiredForStageId)
        });
        onSubmitSuccess?.();
        handleClose();
      } catch (err: unknown) {
        const e = err as {
          status?: number;
          errors?: { code?: string; message?: string; missing_fields?: Array<{ id?: number; name?: string } | string> };
        };
        if (e.status === 422 && e.errors?.code === "status_transition_validation_failed") {
          const missingFields = e.errors?.missing_fields ?? [];
          const missingNamesRaw = missingFields
            .map((f) => (typeof f === "string" ? f : (f?.name ?? "")))
            .filter(Boolean) as string[];
          // Exclude archived characteristics from the missing list
          const archivedNamesLower = new Set(
            state.characteristics.filter((c) => c.is_deleted).map((c) => c.name.trim().toLowerCase())
          );
          const missingNames = missingNamesRaw.filter((name) => !archivedNamesLower.has(name.trim().toLowerCase()));

          if (missingNames.length > 0) {
            const firstMissingName = missingNames[0];
            const key = Object.keys(missingFieldRefsMapRef.current).find(
              (k) => k.trim().toLowerCase() === firstMissingName.trim().toLowerCase()
            );
            const el = key ? missingFieldRefsMapRef.current[key] : null;
            if (el) {
              setTimeout(() => {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                const focusable = el.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
                  "input:not([disabled]), textarea:not([disabled]), select:not([disabled])"
                );
                focusable?.focus();
              }, 100);
            }
            showCannotMoveToStageModal({
              dealId,
              targetStageName: targetStageName ?? "this stage",
              targetStageId: Number(requiredForStageId),
              missingFields: missingNames,
              alreadyOnDeal: true
            });
          }
          // If missingNames is empty (all were archived), do not show modal
        } else {
          errorModal({ msg: "Something went wrong" });
        }
      }
    } else {
      handleClose();
    }
  }, [
    dealId,
    state,
    subAssetClasses,
    targetStageName,
    requiredForStageId,
    missingFieldNamesForContext,
    isDealContext,
    getDealAndStoreAction,
    updateDealAction,
    onSubmitSuccess,
    showCannotMoveToStageModal,
    errorModal
  ]);

  const handleChangeSubCategory = ({
    field,
    value
  }: {
    field: keyof Pick<State, "characteristic_subcategory_id">;
    value: number;
  }) => {
    setState((prevState) => ({ ...prevState, [field]: value }));
  };

  useEffect(() => {
    setViewBy(null);
    // if (dealId && isOpen && !viewOnly) {
    if (!dealId) return;
    getData(dealId);
    getAssetClasses();
  }, [dealId]);

  useEffect(() => {
    missingFieldRefsMapRef.current = {};
    if (!dealId) hasScrolledToMissingRef.current = false;
  }, [dealId, missingFieldNamesForContext]);

  // Scroll to first missing field and focus its input after content is mounted (loading done); runs once per drawer open
  useEffect(() => {
    if (
      hasScrolledToMissingRef.current ||
      !missingFieldNamesForContext?.length ||
      loading ||
      assetClassesLoading ||
      !state.characteristics.length
    )
      return;
    const id = setTimeout(() => {
      const firstKey = missingFieldNamesForContext[0]?.trim().toLowerCase();
      const map = missingFieldRefsMapRef.current;
      const scrollTarget =
        (firstKey && map[firstKey]) ?? Object.values(map).find((el): el is HTMLDivElement => el != null);
      if (!scrollTarget || hasScrolledToMissingRef.current) return;
      hasScrolledToMissingRef.current = true;

      const scrollParent = scrollTarget instanceof HTMLElement ? findScrollParent(scrollTarget) : null;
      if (scrollParent) {
        const targetRect = scrollTarget.getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();
        const targetOffsetTop = scrollParent.scrollTop + (targetRect.top - parentRect.top);
        const newScrollTop = targetOffsetTop - scrollParent.clientHeight / 2 + scrollTarget.offsetHeight / 2;
        scrollParent.scrollTo({ top: Math.max(0, newScrollTop), behavior: "smooth" });
      } else {
        scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      const focusable = scrollTarget.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        "input:not([disabled]), textarea:not([disabled]), select:not([disabled])"
      );
      focusable?.focus();
    }, 400);
    return () => clearTimeout(id);
  }, [loading, assetClassesLoading, missingFieldNamesForContext, state.characteristics.length, state.category]);

  const formContextValue = useMemo(
    () => ({
      getCharacteristics,
      getOneData,
      getManyData,
      createChar,
      updateChar,
      deleteChar,
      updateCharData,
      buildNewCharData,
      handleManyCharData,
      setCharacteristicRef,
      overrideCharacteristicData,
      isOverrideAvailable,
      viewBy,
      ...(dealId && { dealId }),
      missingFieldNames: missingFieldNamesForContext,
      missingFieldRefsMap: missingFieldRefsMapRef
    }),
    [
      getCharacteristics,
      getOneData,
      getManyData,
      createChar,
      updateChar,
      deleteChar,
      updateCharData,
      buildNewCharData,
      handleManyCharData,
      setCharacteristicRef,
      overrideCharacteristicData,
      isOverrideAvailable,
      viewBy,
      dealId,
      missingFieldNamesForContext,
      missingFieldRefsMapRef
    ]
  );

  return (
    <Drawer
      open={Boolean(dealId)}
      onClose={handleClose}
      sx={{
        ".MuiPaper-root": {
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "stretch",
          borderTopRightRadius: "25px",
          borderBottomRightRadius: "25px",
          height: "100%",
          overflow: "hidden",
          minWidth: "550px",
          maxWidth: "550px",
          backgroundColor: "white"
        }
      }}
    >
      <SimpleHeadMui onIconClick={handleClose}>Deal Characteristics</SimpleHeadMui>
      <AsyncContentMui isLoading={loading || assetClassesLoading} spinnerProps={{ size: 50 }}>
        <FormContext.Provider value={formContextValue}>
          <ProgressOverlay
            inProgress={postLoading}
            containerSX={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              padding: "20px 0px 20px 20px",
              overflow: "auto",
              gap: "20px"
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ paddingRight: "15px" }}
              gap="20px"
            >
              <MaterialTooltip title={dealName} isShow>
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: "500",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap"
                  }}
                >
                  {dealName}
                </Typography>
              </MaterialTooltip>
              {investor_level_characteristics && dealId && (
                <ViewSelector
                  dealId={dealId}
                  disabled={!isManagerUser || Boolean(viewOnly)}
                  viewBy={viewBy}
                  setViewBy={setViewBy}
                />
              )}
            </Stack>

            <CategoriesTabs
              categories={data?.categories.filter((category) => category !== "Asset Report") || []}
              value={state.category}
              onChange={handleChangeTab}
            />
            <Stack sx={{ overflow: "auto", padding: "10px 20px 10px 0", gap: "20px" }}>
              {state.category === "General" && (
                <SelectField
                  label="Asset Class"
                  disabled={!isManagerUser || viewOnly || Boolean(viewBy) || featureViewOnly}
                  value={state.assetClassId}
                  isShow={state.showAssetClass}
                  onChange={(e) => handleShowAssets({ field: "assetClassId", value: Number(e.target.value) })}
                  onIsShow={() => handleShowAssets({ field: "showAssetClass", value: !state.showAssetClass })}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }
                  }}
                >
                  {assetClasses?.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.account_alias}
                    </MenuItem>
                  ))}
                </SelectField>
              )}
              {state.category === "General" && (
                <SelectField
                  {...(state.subAssetClassId && {
                    onClear: () => handleShowAssets({ field: "subAssetClassId", value: null })
                  })}
                  label="Sub Asset Class"
                  disabled={!isManagerUser || viewOnly || Boolean(viewBy) || featureViewOnly}
                  value={state.subAssetClassId}
                  isShow={state.showSubAssetClass}
                  onChange={(e) => handleShowAssets({ field: "subAssetClassId", value: Number(e.target.value) })}
                  onIsShow={() => handleShowAssets({ field: "showSubAssetClass", value: !state.showSubAssetClass })}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }
                  }}
                >
                  {subAssetClasses.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.account_alias}
                    </MenuItem>
                  ))}
                </SelectField>
              )}
              {state.category === "General" && show_debt && (
                <Stack direction="row" spacing={2}>
                  <Stack direction="row" spacing={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.isDebt}
                          disabled={viewOnly || Boolean(viewBy) || featureViewOnly}
                          onChange={(e) => setState((prevState) => ({ ...prevState, isDebt: e.target.checked }))}
                        />
                      }
                      label="Debt Deal"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.hasSubDeals}
                          disabled={viewOnly || Boolean(viewBy) || featureViewOnly}
                          onChange={(e) => setState((prevState) => ({ ...prevState, hasSubDeals: e.target.checked }))}
                        />
                      }
                      label="Include Sub Deals"
                    />
                  </Stack>
                </Stack>
              )}
              {state.category !== "Contact" && (
                <Category categoryName={state.category} viewOnly={viewOnly || !isManagerUser || featureViewOnly} />
              )}
              {state.category === "Contact" && (
                <ContactList
                  state={state}
                  setState={setState}
                  viewOnly={viewOnly || !isManagerUser || featureViewOnly}
                />
              )}

              {state.category === "Asset Report" && (
                <AssetReport
                  disabled={!isManagerUser || viewOnly || Boolean(viewBy)}
                  categories={state.subCategories}
                  subCategoryId={state.characteristic_subcategory_id}
                  viewOnly={viewOnly || !isManagerUser || featureViewOnly}
                  onChange={(e) =>
                    handleChangeSubCategory({
                      field: "characteristic_subcategory_id",
                      value: Number(e.target.value)
                    })
                  }
                />
              )}
              {(featureAdmin || isAdminRole) &&
                !viewOnly &&
                state.category !== "Contact" &&
                state.category !== "Asset Report" && (
                  <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                    <CreateChar />
                  </RoleComponentGuard>
                )}
            </Stack>
          </ProgressOverlay>
        </FormContext.Provider>

        {!viewOnly && !featureViewOnly && (
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
            <Stack
              sx={{
                flexDirection: "row",
                justifyContent: "flex-end",
                alignItems: "center",
                height: "87px",
                padding: "30px",
                boxShadow: "0 -4px 13px 0 rgba(10, 49, 66, 0.2)",
                gap: "15px"
              }}
            >
              <Button onClick={handleClose} variant="outlined">
                Cancel
              </Button>
              <Button ref={submitBtnRef} disabled={postLoading} variant="contained" onClick={handleSubmit}>
                {targetStageName ? `Apply and move to ${targetStageName}` : "Apply"}
              </Button>
            </Stack>
          </RoleComponentGuard>
        )}
      </AsyncContentMui>
    </Drawer>
  );
};
